import React, { useState, useEffect } from 'react'
import { X, Calendar, Users, DollarSign, TrendingUp, CheckCircle, AlertTriangle, Download } from 'lucide-react'
import { formatCurrency } from '../utils/currency'
import { formatDate } from '../utils/date'

interface Payslip {
  id: number
  employee: {
    employeeNumber: string
    firstName: string
    lastName: string
    department: string
    position: string
  }
  grossSalary: number
  netPay: number
  salaryCurrency: string
  incomeTax: number
  socialSecurity: number
  totalDeductions: number
  workingDays: number
  leaveDaysTaken: number
  overtimeAmount: number
  bonusAmount: number
  allowancesTotal: number
  loanDeductions: number
  otherDeductions: number
}

interface PayrollRunDetailsModalProps {
  payrollRun: any
  onClose: () => void
  onApproved: () => void
}

const PayrollRunDetailsModal: React.FC<PayrollRunDetailsModalProps> = ({ 
  payrollRun, 
  onClose, 
  onApproved 
}) => {
  const [payslips, setPayslips] = useState<Payslip[]>([])
  const [loading, setLoading] = useState(true)
  const [approving, setApproving] = useState(false)

  const canApprove = false // Payroll runs are automatically completed, no manual approval needed

  const fetchPayrollDetails = async () => {
    try {
      const token = localStorage.getItem('accessToken')
      const response = await fetch(`/api/v1/payroll/runs/${payrollRun.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const result = await response.json()
        setPayslips(result.data.payrollRun.payslips || [])
      }
    } catch (error) {
      console.error('Error fetching payroll details:', error)
    } finally {
      setLoading(false)
    }
  }

  const downloadPayslipPDF = async (payslipId: number, employeeNumber: string) => {
    try {
      const token = localStorage.getItem('accessToken')
      const response = await fetch(`/api/v1/payroll/payslips/${payslipId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `payslip_${employeeNumber}_${payrollRun.payrollMonth}-${payrollRun.payrollYear}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        const error = await response.json()
        alert(error.message || 'Failed to download payslip PDF')
      }
    } catch (error) {
      console.error('Error downloading payslip PDF:', error)
      alert('Failed to download payslip PDF')
    }
  }

  useEffect(() => {
    fetchPayrollDetails()
  }, [payrollRun.id])

  const handleApprove = async () => {
    setApproving(true)
    try {
      const token = localStorage.getItem('accessToken')
      const response = await fetch(`/api/v1/payroll/runs/${payrollRun.id}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        onApproved()
        onClose()
      } else {
        const error = await response.json()
        alert(error.message || 'Failed to approve payroll run')
      }
    } catch (error) {
      console.error('Error approving payroll:', error)
      alert('Failed to approve payroll run')
    } finally {
      setApproving(false)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-blue-500" />
      case 'failed':
        return <AlertTriangle className="h-5 w-5 text-red-500" />
      case 'completed_with_errors':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      default:
        return <div className="h-5 w-5 bg-gray-400 rounded-full animate-pulse"></div>
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'processing':
        return 'Processing'
      case 'completed':
        return 'Completed'
      case 'approved':
        return 'Approved'
      case 'failed':
        return 'Failed'
      case 'completed_with_errors':
        return 'Completed with Errors'
      default:
        return status
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'approved':
        return 'bg-blue-100 text-blue-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      case 'completed_with_errors':
        return 'bg-yellow-100 text-yellow-800'
      case 'processing':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Calendar className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Payroll Run Details</h2>
              <p className="text-sm text-gray-500">
                {formatDate(payrollRun.periodStart)} - {formatDate(payrollRun.periodEnd)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center">
                <Users className="h-6 w-6 text-blue-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-blue-600">Employees</p>
                  <p className="text-2xl font-bold text-blue-900">{payrollRun.totalEmployees}</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center">
                <DollarSign className="h-6 w-6 text-green-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-600">Gross Pay</p>
                  <p className="text-lg font-bold text-green-900">
                    {formatCurrency(payrollRun.totalGrossPay, 'USD')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="flex items-center">
                <TrendingUp className="h-6 w-6 text-purple-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-purple-600">Net Pay</p>
                  <p className="text-lg font-bold text-purple-900">
                    {formatCurrency(payrollRun.totalNetPay, 'USD')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center">
                <TrendingUp className="h-6 w-6 text-red-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-red-600">Total Deductions</p>
                  <p className="text-lg font-bold text-red-900">
                    {formatCurrency(payrollRun.totalDeductions, 'USD')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status and Actions */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              {getStatusIcon(payrollRun.status)}
              <span className={`ml-2 px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(payrollRun.status)}`}>
                {getStatusText(payrollRun.status)}
              </span>
              <span className="ml-4 text-sm text-gray-500">
                Processed by {payrollRun.processedByUser.firstName} {payrollRun.processedByUser.lastName}
              </span>
            </div>

            <div className="flex space-x-3">
              {canApprove && (
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {approving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {approving ? 'Approving...' : 'Approve Payroll'}
                </button>
              )}
            </div>
          </div>

          {/* Payslips Table */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Employee Payslips</h3>
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading payslips...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Salary</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tax</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deductions</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Salary</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {payslips.map((payslip) => (
                      <tr key={payslip.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {payslip.employee.firstName} {payslip.employee.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{payslip.employee.employeeNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{payslip.employee.department}</div>
                          <div className="text-sm text-gray-500">{payslip.employee.position}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(payslip.grossSalary, payslip.salaryCurrency)}
                          </div>
                          {payslip.bonusAmount > 0 && (
                            <div className="text-xs text-green-600">
                              +{formatCurrency(payslip.bonusAmount, payslip.salaryCurrency)} bonus
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatCurrency(payslip.totalDeductions, payslip.salaryCurrency)}
                          </div>
                          <div className="text-xs text-gray-500">
                            IPR: {formatCurrency(payslip.incomeTax, payslip.salaryCurrency)}
                          </div>
                          <div className="text-xs text-gray-500">
                            CNSS: {formatCurrency(payslip.socialSecurity, payslip.salaryCurrency)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatCurrency(payslip.loanDeductions + payslip.otherDeductions, payslip.salaryCurrency)}
                          </div>
                          {payslip.loanDeductions > 0 && (
                            <div className="text-xs text-gray-500">
                              Loan: {formatCurrency(payslip.loanDeductions, payslip.salaryCurrency)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(payslip.netPay, payslip.salaryCurrency)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Processed
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => downloadPayslipPDF(
                              payslip.id,
                              payslip.employee.employeeNumber
                            )}
                            className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                            title="Download PDF payslip"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {payslips.length === 0 && !loading && (
              <div className="p-8 text-center text-gray-500">
                No payslips found for this payroll run
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

export default PayrollRunDetailsModal
